import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';

import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../../theme/tokens';
import { EntityPanelFrame } from './EntityPanelFrame';
import { useEntityPageModel } from './useEntityPageModel';

const descriptions = {
  apiReference: 'Interactive schema and request details',
  examples: 'Example requests and common payloads',
  techDocs: 'Architecture guides, how-tos, and service documentation',
} as const;

const isExternalLink = (value: string) => /^https?:\/\//.test(value);

const EntityHowToUseLinkRow = ({
  description,
  href,
  icon,
  label,
}: {
  description: string;
  href: string;
  icon: React.ReactNode;
  label: string;
}) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const external = isExternalLink(href);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'grid',
        gap: 1.25,
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: getSurfaceColor(theme, 'subtle'),
          border: `1px solid ${getSurfaceBorder(theme)}`,
          borderRadius: `${radii.md}px`,
          color: theme.palette.primary.main,
          display: 'inline-flex',
          height: 36,
          justifyContent: 'center',
          width: 36,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: tokens.textMuted,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>
      </Box>

      <Link
        component={external ? 'a' : RouterLink}
        href={external ? href : undefined}
        rel={external ? 'noreferrer' : undefined}
        sx={{
          alignItems: 'center',
          color: theme.palette.primary.main,
          display: 'inline-flex',
          fontSize: 14,
          fontWeight: 700,
          gap: 0.625,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
        }}
        target={external ? '_blank' : undefined}
        to={external ? undefined : href}
        underline="none"
      >
        Open
        <OpenInNewOutlinedIcon fontSize="inherit" />
      </Link>
    </Box>
  );
};

const EntityDetailRow = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);

  return (
    <Box sx={{ display: 'grid', gap: 0.875 }}>
      <Typography
        sx={{
          color: tokens.textMuted,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.06em',
          lineHeight: 1.2,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: getSurfaceColor(theme, 'subtle'),
          border: `1px solid ${getSurfaceBorder(theme)}`,
          borderRadius: `${radii.md}px`,
          color: tokens.textSecondary,
          display: 'flex',
          fontFamily: 'Menlo, Monaco, Consolas, monospace',
          fontSize: 13,
          minHeight: 42,
          px: 1.5,
          py: 1,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </Box>
    </Box>
  );
};

export const EntityHowToUsePanel = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const model = useEntityPageModel();

  const apiTabPath = model.howToUse.apiTab.path;
  const apiReference = model.howToUse.apiReference;
  const examples = model.howToUse.examples;
  const techDocsPath = model.documentation.techDocs.path;

  return (
    <EntityPanelFrame title="How to use (for other teams)">
      <Stack spacing={2.5}>
        <Typography
          sx={{
            color: tokens.textSecondary,
            fontSize: 16,
            lineHeight: 1.7,
          }}
        >
          Use the available API and documentation entry points below. Convention
          fields like base URL and authentication will appear here automatically
          once they are standardized in entity metadata.
        </Typography>

        <Stack spacing={1.75}>
          {techDocsPath ? (
            <EntityHowToUseLinkRow
              description={descriptions.techDocs}
              href={techDocsPath}
              icon={<MenuBookOutlinedIcon fontSize="small" />}
              label="TechDocs"
            />
          ) : null}

          {apiReference ? (
            <EntityHowToUseLinkRow
              description={descriptions.apiReference}
              href={apiReference.url}
              icon={<ApiOutlinedIcon fontSize="small" />}
              label={apiReference.title}
            />
          ) : null}

          {examples ? (
            <EntityHowToUseLinkRow
              description={descriptions.examples}
              href={examples.url}
              icon={<TerminalOutlinedIcon fontSize="small" />}
              label={examples.title}
            />
          ) : null}

          {!techDocsPath && !apiReference && !examples && apiTabPath ? (
            <EntityHowToUseLinkRow
              description="Open the entity API route for related definitions and usage details"
              href={apiTabPath}
              icon={<RouteOutlinedIcon fontSize="small" />}
              label="API tab"
            />
          ) : null}

          {!techDocsPath && !apiReference && !examples && !apiTabPath ? (
            <Typography
              sx={{
                color: tokens.textMuted,
                fontSize: 14,
                lineHeight: 1.7,
              }}
            >
              No API reference or example links are configured for this entity
              yet.
            </Typography>
          ) : null}
        </Stack>

        {model.howToUse.baseUrl.value || model.howToUse.authentication.value ? (
          <Stack spacing={2}>
            {model.howToUse.baseUrl.value ? (
              <EntityDetailRow
                label="Base URL"
                value={model.howToUse.baseUrl.value}
              />
            ) : null}

            {model.howToUse.authentication.value ? (
              <EntityDetailRow
                label="Authentication"
                value={model.howToUse.authentication.value}
              />
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </EntityPanelFrame>
  );
};
